// ============================================================================
// Roof Designer — Satelliten-Hintergrund (pure Tile-Mathematik, kein DOM)
// ----------------------------------------------------------------------------
// Rechnet aus einer BackgroundGeoRef (invertierbare Canvas↔WGS84-Abbildung,
// siehe types.ts) die Esri-World-Imagery-Tiles samt Zeichenpositionen für den
// sichtbaren Canvas-Ausschnitt aus. Gleiche Tile-Quelle wie der Gebäude-Editor
// (RoofDrawMap) — kein API-Key, Attribution Pflicht ("© Esri, Maxar, USGS").
//
// Genauigkeit: Die Designer-Abbildung ist äquirektangular um lat0/lng0, Tiles
// sind WebMercator. Über typische Dach-Viewports (< 100 m) ist die Abweichung
// << 1 Pixel — für Planungs-Kontext (KEIN Vermessungsersatz) völlig ausreichend.
// Der Hintergrund ist Orientierung, niemals Nachweis: ENTWURF-Charakter bleibt.
// ============================================================================
import type { BackgroundGeoRef } from "./types.js";

export const ESRI_TILE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
export const ESRI_ATTRIBUTION = "© Esri, Maxar, USGS";
export const ESRI_MAX_ZOOM = 19;

const TILE_SIZE = 256;
const M_PER_DEG_LAT = 111_320;
// WebMercator-Bodenauflösung am Äquator bei z=0 (m / Tile-Pixel).
const EQUATOR_RES_Z0 = 156_543.033_92;

export interface TileDrawInstruction {
  url: string;
  /** Tile-Koordinaten (für Cache-Key/Debug). */
  z: number;
  x: number;
  y: number;
  /** Zeichenposition im Canvas (Pixel, kann Subpixel sein). */
  dx: number;
  dy: number;
  /** Zeichengröße im Canvas (Pixel) — Tiles werden hochskaliert. */
  dSize: number;
}

export interface TileLayout {
  zoom: number;
  tiles: TileDrawInstruction[];
}

/** Canvas-Pixel → WGS84 (Inverse der import-surface-Abbildung). */
export function pixelToLatLng(
  ref: BackgroundGeoRef,
  px: number,
  py: number,
): { lat: number; lng: number } {
  const mPerDegLng = M_PER_DEG_LAT * Math.cos((ref.lat0 * Math.PI) / 180);
  const xM = (px - ref.marginPx) * ref.metersPerPixel + ref.minXM;
  const yM = (py - ref.marginPx) * ref.metersPerPixel + ref.minYM;
  return {
    lng: ref.lng0 + xM / mPerDegLng,
    lat: ref.lat0 - yM / M_PER_DEG_LAT,
  };
}

/** WGS84 → globale WebMercator-Pixelkoordinaten bei Zoom z. */
export function latLngToWorldPx(
  lat: number,
  lng: number,
  zoom: number,
): { x: number; y: number } {
  const scale = TILE_SIZE * Math.pow(2, zoom);
  const x = ((lng + 180) / 360) * scale;
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
  return { x, y };
}

/** Bodenauflösung (m / Tile-Pixel) bei gegebener Breite und Zoom. */
export function groundResolution(lat: number, zoom: number): number {
  return (EQUATOR_RES_Z0 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
}

/**
 * Höchste sinnvolle Zoomstufe: native Tile-Auflösung ≥ Canvas-Auflösung,
 * gedeckelt auf ESRI_MAX_ZOOM. (Dach-Maßstäbe liegen praktisch immer über
 * z19-Auflösung → Tiles werden hochskaliert; das ist ehrlicher Kontext.)
 */
export function chooseZoom(ref: BackgroundGeoRef): number {
  const ideal = Math.ceil(
    Math.log2((EQUATOR_RES_Z0 * Math.cos((ref.lat0 * Math.PI) / 180)) / ref.metersPerPixel),
  );
  return Math.max(1, Math.min(ESRI_MAX_ZOOM, ideal));
}

/**
 * Berechnet alle Tiles, die den Canvas-Ausschnitt [0,0..wPx,hPx] abdecken,
 * inkl. Zeichenposition/-größe. Begrenzt defensiv auf 12×12 Tiles.
 */
export function computeTileLayout(
  ref: BackgroundGeoRef,
  widthPx: number,
  heightPx: number,
): TileLayout {
  const zoom = chooseZoom(ref);
  // Skalierung: 1 Tile-Pixel entspricht groundRes Metern; der Canvas zeigt
  // metersPerPixel Meter pro Canvas-Pixel.
  const canvasPxPerTilePx = groundResolution(ref.lat0, zoom) / ref.metersPerPixel;

  // Welt-Pixel des Canvas-Ursprungs (0,0):
  const origin = pixelToLatLng(ref, 0, 0);
  const w0 = latLngToWorldPx(origin.lat, origin.lng, zoom);

  const maxTileIndex = Math.pow(2, zoom) - 1;
  const tileCanvasSize = TILE_SIZE * canvasPxPerTilePx;

  const firstTx = Math.floor(w0.x / TILE_SIZE);
  const firstTy = Math.floor(w0.y / TILE_SIZE);
  const lastTx = Math.floor((w0.x + widthPx / canvasPxPerTilePx) / TILE_SIZE);
  const lastTy = Math.floor((w0.y + heightPx / canvasPxPerTilePx) / TILE_SIZE);

  const tiles: TileDrawInstruction[] = [];
  for (let ty = firstTy; ty <= Math.min(lastTy, firstTy + 11); ty++) {
    for (let tx = firstTx; tx <= Math.min(lastTx, firstTx + 11); tx++) {
      if (tx < 0 || ty < 0 || tx > maxTileIndex || ty > maxTileIndex) continue;
      tiles.push({
        url: ESRI_TILE_URL.replace("{z}", String(zoom)).replace("{y}", String(ty)).replace("{x}", String(tx)),
        z: zoom,
        x: tx,
        y: ty,
        dx: (tx * TILE_SIZE - w0.x) * canvasPxPerTilePx,
        dy: (ty * TILE_SIZE - w0.y) * canvasPxPerTilePx,
        dSize: tileCanvasSize,
      });
    }
  }
  return { zoom, tiles };
}

/**
 * Hochgeladenes Bild → herunterskalierte JPEG-DataURL (persistierbar in
 * designJson/localStorage, im Gegensatz zu flüchtigen Object-URLs).
 * Läuft im Browser (Canvas-API) — im Test-Umfeld nicht aufrufbar, daher
 * bewusst von der reinen Tile-Mathematik getrennt gehalten.
 */
export async function fileToScaledDataUrl(
  file: File,
  maxDimensionPx = 1600,
  quality = 0.85,
): Promise<string> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("Bild konnte nicht gelesen werden."));
      i.src = objectUrl;
    });
    const scale = Math.min(1, maxDimensionPx / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas nicht verfügbar.");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", quality);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
